import { assertNever, type GridPos, type GridSnapshot, type SimEvent } from "@reagent/sim";
import { PALETTE } from "../palette";
import { gridToWorld } from "../iso/gridToWorld";
import type { FxInstance, Vec3 } from "./types";

/**
 * Translates simulation events into visual effects. This switch is
 * exhaustive over SimEvent: adding an event to the protocol without deciding
 * what it looks like is a compile error here, which is the whole reason the
 * art prototype is built against the real event union.
 *
 * Returning `[]` is a legitimate decision (the effect is a state change the
 * snapshot already renders, e.g. a corroded pipe darkening) — but it has to
 * be an explicit one.
 */

let fxCounter = 0;
const nextId = () => `fx-${++fxCounter}`;

interface Resolver {
  nodeAt: (nodeId: string) => GridPos | null;
  edgeMidpoint: (edgeId: string) => GridPos | null;
  hopper: () => GridPos | null;
}

export function makeResolver(snapshot: GridSnapshot): Resolver {
  return {
    nodeAt: (nodeId) => snapshot.nodes.find((n) => n.id === nodeId)?.pos ?? null,
    hopper: () => snapshot.nodes.find((n) => n.kind === "productHopper")?.pos ?? null,
    edgeMidpoint: (edgeId) => {
      const edge = snapshot.edges.find((e) => e.id === edgeId);
      if (!edge || edge.path.length === 0) return null;
      return edge.path[Math.floor(edge.path.length / 2)] ?? null;
    },
  };
}

const world = (pos: GridPos, height = 0.3): Vec3 => gridToWorld(pos, height);

export function fxForEvent(event: SimEvent, resolve: Resolver): FxInstance[] {
  switch (event.type) {
    case "packet.moved":
      // Nothing, deliberately. Packets are no longer drawn as effects at all:
      // a position is state, so PacketsInFlight renders them straight from the
      // snapshot and eases between ticks. Spawning a blob here as well would
      // draw every travelling packet twice for the length of its hop.
      return [];

    case "packet.merged":
      return [
        { id: nextId(), kind: "mergeFlash", at: world(event.at, 0.4), color: PALETTE.fluidMagenta, duration: 0.5 },
      ];

    case "packet.phaseConverted": {
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [
        {
          id: nextId(),
          kind: "reactionFlash",
          at: world(pos, 0.6),
          color: PALETTE.fluidSkyBlue,
          duration: 0.55,
        },
      ];
    }

    case "packet.separated": {
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [
        {
          id: nextId(),
          kind: "reactionFlash",
          at: world(pos, 0.6),
          color: PALETTE.fluidSkyBlue,
          duration: 0.55,
        },
      ];
    }

    case "reaction.fired": {
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [
        {
          id: nextId(),
          kind: "reactionFlash",
          at: world(pos, 0.6),
          color: event.isExothermic ? PALETTE.hazardWarning : PALETTE.fluidMintGreen,
          duration: 0.6,
        },
      ];
    }

    case "node.heated": {
      const pos = resolve.nodeAt(event.nodeId);
      // Only shimmer once heat is actually meaningful, or every tick smears.
      if (!pos || event.heatCounter / Math.max(event.criticalTemp, 1) < 0.5) return [];
      return [{ id: nextId(), kind: "heatShimmer", at: world(pos, 0.7), duration: 0.9 }];
    }

    case "node.overheatWarning": {
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [
        { id: nextId(), kind: "warningPulse", at: world(pos, 0.55), color: PALETTE.hazardCritical, duration: 1.0 },
      ];
    }

    case "node.exploded": {
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [
        { id: nextId(), kind: "explosion", at: world(pos, 0.5), color: PALETTE.hazardSpark, duration: 1.1 },
      ];
    }

    case "pipe.pressured": {
      const pos = resolve.edgeMidpoint(event.edgeId);
      if (!pos) return [];
      return [{ id: nextId(), kind: "pressureJudder", at: world(pos, 0.2), duration: 0.35 }];
    }

    case "pipe.burst":
      return [
        { id: nextId(), kind: "burstSpill", at: world(event.spillAt, 0.2), color: PALETTE.fluidMagenta, duration: 1.2 },
      ];

    case "pipe.corroded": {
      const pos = resolve.edgeMidpoint(event.edgeId);
      if (!pos) return [];
      return [{ id: nextId(), kind: "corrosionMark", at: world(pos, 0.22), duration: 0.6 }];
    }

    case "pipe.leaking": {
      const pos = resolve.edgeMidpoint(event.edgeId);
      if (!pos) return [];
      return [
        { id: nextId(), kind: "leakDribble", at: world(pos, 0.18), color: PALETTE.fluidMagenta, duration: 1.4 },
      ];
    }

    case "catalyst.poisoned": {
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [{ id: nextId(), kind: "poisonPuff", at: world(pos, 0.6), color: PALETTE.sageDark, duration: 1.0 }];
    }

    case "packet.sludged": {
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [{ id: nextId(), kind: "sludgeBubble", at: world(pos, 0.5), duration: 1.3 }];
    }

    case "card.played": {
      if (!event.at) return [];
      return [{ id: nextId(), kind: "cardSlam", at: world(event.at, 0.05), duration: 0.5 }];
    }

    case "objective.progressed": {
      // Over the hopper the product actually landed in, not a fixed point:
      // stages put their hopper in different places.
      const hopper = resolve.hopper();
      if (!hopper) return [];
      return [
        {
          id: nextId(),
          kind: "objectiveTick",
          at: world(hopper, 1.1),
          color: PALETTE.fluidMintGreen,
          duration: 0.8,
        },
      ];
    }

    case "tile.contaminated": {
      // Chemical spill / contamination flash at the affected tile, reusing the
      // same visual vocabulary as burst/leak fluid effects. Tinted hazard-red
      // to distinguish from the liquid magenta of fluid spills.
      return [
        { id: nextId(), kind: "burstSpill", at: world(event.pos, 0.15), color: PALETTE.hazardCritical, duration: 1.0 },
      ];
    }

    case "facility.verminRisk":
      // Facility-wide pest counter increment has no board position — the threat
      // is abstract, announced via the HUD and toast, like deck.polluted and
      // phase changes below.
      return [];

    case "node.infested": {
      // Rodent infestation of a processing node reads as machinery disablement, not
      // catalyst poisoning: use poisonPuff but tint it as "broken" (chassis grey)
      // rather than sage, so the two hazards are visually distinct.
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [
        { id: nextId(), kind: "poisonPuff", at: world(pos, 0.6), color: PALETTE.chassisGreyDark, duration: 1.0 },
      ];
    }

    case "node.infestationCleared": {
      // The infestation ended (starved or fumigated). The hazard cleared, so reuse
      // the same relief-read FX vocabulary as other hazard clearances: a light
      // mint-tinted burst at the node's position.
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [
        { id: nextId(), kind: "burstSpill", at: world(pos, 0.15), color: PALETTE.fluidMintGreen, duration: 1.0 },
      ];
    }

    case "facility.powerOutage": {
      // Hardware freeze: electrical outage disables equipment. The event names
      // multiple nodes, so spawn a poisonPuff effect at each frozen node's
      // position, reusing the machinery-disabled visual language (broken chassis grey).
      const effects: FxInstance[] = [];
      for (const nodeId of event.frozenNodeIds) {
        const pos = resolve.nodeAt(nodeId);
        if (pos) {
          effects.push({
            id: nextId(),
            kind: "poisonPuff",
            at: world(pos, 0.6),
            color: PALETTE.chassisGreyDark,
            duration: 1.0,
          });
        }
      }
      return effects;
    }

    case "facility.disaster":
      // Environmental/structural disaster is a facility-wide announcement with
      // no board position — like facility.verminRisk and deck pollution, it is
      // announced via HUD (telegraph of upcoming event) and audio, not as
      // in-world visual effects.
      return [];

    case "hazard.cardPenalty":
      // A held hazard card's recurring cost (kW, draw size, pipe HP, ...) is a
      // hand/HUD-level readout, not a board effect — announced via card-face
      // text and HUD readouts (AC20). `targetId` is nullable and, when present,
      // is a node id, edge id, or tile key of mixed provenance that the FX
      // Resolver cannot look up without new lookup logic this spec doesn't ask
      // for, so this gets the same treatment as facility.verminRisk /
      // facility.disaster / deck.polluted: no board position, no FX.
      return [];

    case "hazard.cardCleared":
      // A clearsOnPlay hazard card (Clogged Line, Chemical Hazard Spill) just
      // released its lock. When it named a tile, that is exactly
      // tile.contaminated's shape in reverse, so it reuses the same burstSpill
      // FX at that position — tinted mint green rather than hazard red, so a
      // clearing burst reads as "resolved" rather than "hazard occurring".
      // A global clear (`at: null`) has no board position, same as above.
      return event.at
        ? [{ id: nextId(), kind: "burstSpill", at: world(event.at, 0.15), color: PALETTE.fluidMintGreen, duration: 1.0 }]
        : [];

    case "node.regulated": {
      // A thermalRegulator applied its ΔT to its buffer. Reuse heatShimmer with
      // a temperature-direction tint: warm (hazardWarning) for heating, cool
      // (fluidSkyBlue) for chilling. Return nothing if no packets were touched.
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos || event.packetCount === 0) return [];
      const color = event.deltaTemp > 0 ? PALETTE.hazardWarning : PALETTE.fluidSkyBlue;
      return [{ id: nextId(), kind: "heatShimmer", at: world(pos, 0.7), color, duration: 0.9 }];
    }

    case "node.valveSet": {
      // A player toggled a flowMover's routing setting. Reuse pressureJudder
      // at the node's position — the valve throw reads as "something clunked
      // into place" without inventing new FX, and the judder is already a
      // mechanical toggle metaphor (pipe strain, not chemical reaction).
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [{ id: nextId(), kind: "pressureJudder", at: world(pos, 0.3), duration: 0.35 }];
    }

    case "feed.exhausted": {
      // Supply line ran dry: a machine that has stopped. Reuse poisonPuff
      // tinted as broken machinery (chassisGreyDark), same fiction as
      // powerOutage and node.infested.
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [
        { id: nextId(), kind: "poisonPuff", at: world(pos, 0.6), color: PALETTE.chassisGreyDark, duration: 1.0 },
      ];
    }

    case "node.vented": {
      // Vent sink collecting unwanted material. Reuse poisonPuff tinted as
      // waste/dead machinery (chassisGreyDark), same fiction as feed.exhausted
      // and node.infested — something has stopped being useful material.
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos || event.units < 1) return [];
      return [
        { id: nextId(), kind: "poisonPuff", at: world(pos, 0.6), color: PALETTE.chassisGreyDark, duration: 1.0 },
      ];
    }

    case "hardware.installed": {
      // Hardware purchase from the Setup catalogue snaps into place. Reuses
      // cardSlam — installing hardware from the catalogue is conceptually placing
      // a card, just sourced from the shop rather than the hand. A bought pipe
      // slams at its midpoint, the same spot its other edge FX use.
      const pos = event.nodeId !== null ? resolve.nodeAt(event.nodeId) : resolve.edgeMidpoint(event.edgeId ?? "");
      if (!pos) return [];
      return [{ id: nextId(), kind: "cardSlam", at: world(pos, 0.05), duration: 0.5 }];
    }

    case "hardware.dismantled":
      // By the time this event reaches the renderer, the node or pipe has
      // already been removed from the snapshot the resolver was built from, so resolve.nodeAt
      // will return null — a state change the snapshot already renders. Silence
      // is deliberate here: the visual removal is the whole announcement.
      return [];

    case "hardware.moved": {
      // Hardware relocated mid-Play: a snap into place at the destination,
      // reusing cardSlam like hardware.installed. When spilledAt is non-null,
      // tile.contaminated will fire separately and emit its own burstSpill, so
      // we omit the spill FX here to avoid doubling up.
      const destPos = event.to[event.to.length - 1];
      if (!destPos) return [];
      return [{ id: nextId(), kind: "cardSlam", at: world(destPos, 0.05), duration: 0.5 }];
    }

    case "hardware.rebound": {
      // Node recipe rebinding during Play: a snap configuration into place,
      // reusing cardSlam like hardware.moved and hardware.installed. The binding
      // change is a quiet machine adjustment at the node's current position.
      const pos = resolve.nodeAt(event.nodeId);
      if (!pos) return [];
      return [{ id: nextId(), kind: "cardSlam", at: world(pos, 0.05), duration: 0.5 }];
    }

    case "tile.cleaned": {
      // Contaminated tile just cleaned: reuse the hazard-cleared vocabulary, a
      // light burst tinted mint green to read as relief and resolution.
      return [
        { id: nextId(), kind: "burstSpill", at: world(event.pos, 0.15), color: PALETTE.fluidMintGreen, duration: 1.0 },
      ];
    }

    // Phase changes, deck pollution and run end are HUD-level state, rendered
    // by the DOM overlay rather than as in-world effects. Deck pollution in
    // particular has no board position — the deck isn't on the table — so it
    // is announced as a notice instead (see ui/hud/Toast.tsx).
    case "phase.changed":
    case "deck.polluted":
    case "run.failed":
    case "run.cleared":
      return [];

    default:
      return assertNever(event, "fxForEvent");
  }
}
